import React, { useState } from 'react';
import { Modal, Form, Input, message } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ApiError } from '../../../shared/utils/api';
import { authService } from '../services/auth.service';
import { useAuthStore } from '../stores/authStore';

interface ChangePasswordFormValues {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export interface ChangePasswordModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * ChangePasswordModal
 *
 * Allows authenticated users to change their own password.
 * Calls PUT /api/v1/users/:id/password via authService.
 * On success, clears auth state and redirects to login,
 * because the backend revokes all refresh tokens on password change.
 */
export const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  open,
  onClose,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form] = Form.useForm<ChangePasswordFormValues>();
  const [loading, setLoading] = useState(false);

  const userId = useAuthStore((state) => state.user?.id);
  const clearAuth = useAuthStore((state) => state.clearAuth);

  const handleFinish = async (values: ChangePasswordFormValues) => {
    if (userId === undefined || userId === null) {
      message.error(t('common:auth.changePasswordError'));
      return;
    }

    setLoading(true);
    try {
      await authService.changePassword(userId, {
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });
      message.success(t('common:auth.changePasswordSuccess'));
      form.resetFields();
      onClose();
      // Backend revokes all refresh tokens on password change;
      // clear local session and redirect to login.
      clearAuth();
      navigate('/login', { replace: true });
    } catch (error) {
      const errorMsg =
        error instanceof ApiError
          ? error.message
          : t('common:auth.changePasswordError');
      message.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title={t('common:auth.changePassword')}
      open={open}
      onOk={() => { form.submit(); }}
      onCancel={handleCancel}
      confirmLoading={loading}
      okText={t('common:buttons.confirm')}
      cancelText={t('common:buttons.cancel')}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        autoComplete="off"
      >
        <Form.Item
          name="currentPassword"
          label={t('common:auth.currentPassword')}
          rules={[
            { required: true, message: t('common:validation.required') },
          ]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder={t('common:auth.currentPassword')}
            autoComplete="current-password"
          />
        </Form.Item>

        <Form.Item
          name="newPassword"
          label={t('common:auth.newPassword')}
          rules={[
            { required: true, message: t('common:validation.required') },
            { min: 8, message: t('common:validation.minLength', { min: 8 }) },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || value !== getFieldValue('currentPassword')) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  new Error(t('common:validation.sameAsCurrentPassword'))
                );
              },
            }),
          ]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder={t('common:auth.newPassword')}
            autoComplete="new-password"
          />
        </Form.Item>

        <Form.Item
          name="confirmPassword"
          label={t('common:auth.confirmNewPassword')}
          dependencies={['newPassword']}
          rules={[
            { required: true, message: t('common:validation.required') },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('newPassword') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  new Error(t('common:validation.confirmPassword'))
                );
              },
            }),
          ]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder={t('common:auth.confirmNewPassword')}
            autoComplete="new-password"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
